"use client"

import { router } from "@inertiajs/react"
import { DotsGrid, Route } from "@untitledui/icons"
import { type DragEvent, useState } from "react"

import { DestinationStatusBadge } from "@/components/admin/onwards/destination-badges"
import { Button } from "@/components/ui/button"
import { Select } from "@/components/ui/select"
import { SlideoutMenu } from "@/components/ui/slideout-menu"
import { Toggle } from "@/components/ui/toggle"
import {
  adminOnwardsPlacementPath,
  adminOnwardsPlacementsPath,
  adminPlaylistPath,
  reorderAdminOnwardsPlacementsPath,
} from "@/routes"

interface PlacementRow {
  id: number
  slot: string
  position: number
  promotable: boolean
  destination: { name: string; key: string; status: string; kind: string }
}

interface DestinationOption {
  id: number
  kind: string
  name: string
}

export interface AdminOnwards {
  playlistId: number
  published: boolean
  slots: string[]
  placementsBySlot: Record<string, PlacementRow[]>
  destinations: DestinationOption[]
}

const SLOT_LABELS: Record<string, string> = {
  outro: "Outro (the end of the stack)",
  sidebar: "Sidebar",
  stalled_overlay: "Stalled overlay (shown after a long pause)",
}

/**
 * Admin-only flyover layered over the real playlist editor. It opens
 * on arrival, can be dismissed, and the pinned button re-opens it. Visibility
 * and onward placements only; the core fields stay in the page's own form.
 */
export function AdminOnwardsFlyover({
  playlistId,
  published,
  slots,
  placementsBySlot,
  destinations,
}: AdminOnwards) {
  const setPublished = (next: boolean) =>
    router.patch(
      adminPlaylistPath(playlistId),
      { playlist: { published: next } },
      { preserveScroll: true },
    )

  return (
    <SlideoutMenu.Trigger defaultOpen>
      <Button
        color="secondary"
        size="sm"
        iconLeading={Route}
        className="fixed right-5 bottom-5 z-40 shadow-lg"
      >
        Admin controls
      </Button>

      <SlideoutMenu>
        {({ close }: { close: () => void }) => (
          <>
            <SlideoutMenu.Header onClose={close}>
              <div className="flex flex-col gap-0.5 pr-10">
                <p className="text-primary text-lg font-semibold">
                  Admin controls
                </p>
                <p className="text-tertiary text-sm">
                  Set visibility and the onward placements for this playlist.
                </p>
              </div>
            </SlideoutMenu.Header>

            <SlideoutMenu.Content>
              <div className="flex flex-col gap-6">
                <section className="border-secondary flex items-center justify-between gap-4 rounded-xl border p-4">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-primary text-sm font-medium">
                      Public
                    </span>
                    <span className="text-tertiary text-sm">
                      Public playlists show up on Discover. More visibility
                      controls are on the way.
                    </span>
                  </div>
                  <Toggle
                    isSelected={published}
                    onChange={setPublished}
                    aria-label="Public"
                  />
                </section>

                <div className="flex flex-col gap-0.5">
                  <h3 className="text-primary text-sm font-semibold">
                    Onward placements
                  </h3>
                  <p className="text-tertiary text-sm">
                    Place validated destinations on this playlist. Drag to
                    reorder. They run top to bottom, and the first promotable
                    one leads as the continuation.
                  </p>
                </div>

                {slots.map((slot) => (
                  <SlotSection
                    key={slot}
                    slot={slot}
                    label={SLOT_LABELS[slot] ?? slot}
                    rows={placementsBySlot[slot] ?? []}
                    destinations={destinations}
                    playlistId={playlistId}
                  />
                ))}
              </div>
            </SlideoutMenu.Content>

            <SlideoutMenu.Footer>
              <div className="flex justify-end">
                <Button
                  type="button"
                  color="secondary"
                  size="md"
                  onClick={close}
                >
                  Done
                </Button>
              </div>
            </SlideoutMenu.Footer>
          </>
        )}
      </SlideoutMenu>
    </SlideoutMenu.Trigger>
  )
}

function SlotSection({
  slot,
  label,
  rows,
  destinations,
  playlistId,
}: {
  slot: string
  label: string
  rows: PlacementRow[]
  destinations: DestinationOption[]
  playlistId: number
}) {
  const [toAdd, setToAdd] = useState<string | null>(null)
  const [draggingId, setDraggingId] = useState<number | null>(null)
  // Optimistic order, keyed by the server's id-set so a reorder (same ids, new
  // arrangement — the endpoint replies 204, no prop refresh) keeps its local
  // order, while an add/remove (different ids) falls back to the fresh props.
  const [local, setLocal] = useState<{
    sig: string
    rows: PlacementRow[]
  } | null>(null)
  const sig = rows
    .map((r) => r.id)
    .sort((a, b) => a - b)
    .join(",")
  const list = local?.sig === sig ? local.rows : rows

  const drop = (targetId: number) => {
    if (draggingId == null || draggingId === targetId) return
    const from = list.findIndex((r) => r.id === draggingId)
    const to = list.findIndex((r) => r.id === targetId)
    setDraggingId(null)
    if (from === -1 || to === -1) return

    const next = [...list]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    setLocal({ sig, rows: next })
    router.patch(
      reorderAdminOnwardsPlacementsPath(),
      { ordered_ids: next.map((r) => r.id) },
      { preserveScroll: true },
    )
  }

  const add = () => {
    const option = destinations.find((d) => String(d.id) + d.kind === toAdd)
    if (!option) return
    router.post(
      adminOnwardsPlacementsPath(),
      {
        playlist_id: playlistId,
        destination_id: option.id,
        destination_kind: option.kind,
        slot,
      },
      { preserveScroll: true, onSuccess: () => setToAdd(null) },
    )
  }

  const togglePromotable = (row: PlacementRow) =>
    router.patch(
      adminOnwardsPlacementPath(row.id),
      { placement: { promotable: !row.promotable } },
      { preserveScroll: true },
    )

  const remove = (row: PlacementRow) =>
    router.delete(adminOnwardsPlacementPath(row.id), { preserveScroll: true })

  return (
    <section className="border-secondary flex flex-col gap-3 rounded-xl border p-4">
      <h4 className="text-primary text-sm font-semibold">{label}</h4>

      {list.length === 0 ? (
        <p className="text-tertiary text-sm">Nothing placed here yet.</p>
      ) : (
        <ol className="flex flex-col gap-2">
          {list.map((row, index) => (
            <li
              key={row.id}
              draggable
              onDragStart={() => setDraggingId(row.id)}
              onDragOver={(e: DragEvent<HTMLLIElement>) => e.preventDefault()}
              onDrop={() => drop(row.id)}
              className={`border-secondary bg-primary flex flex-col gap-2 rounded-lg border px-3 py-2 ${
                draggingId === row.id ? "opacity-50" : ""
              }`}
            >
              <div className="flex items-center gap-2">
                <DotsGrid
                  aria-hidden="true"
                  className="text-quaternary size-4 shrink-0 cursor-grab active:cursor-grabbing"
                />
                <span className="text-tertiary w-4 shrink-0 text-sm tabular-nums">
                  {index + 1}
                </span>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="text-primary truncate text-sm font-medium">
                    {row.destination.name}
                  </span>
                  <span className="text-tertiary truncate text-xs">
                    {row.destination.key}
                  </span>
                </div>
                <DestinationStatusBadge status={row.destination.status} />
              </div>
              <div className="flex items-center justify-end gap-1">
                <Button
                  size="sm"
                  color={row.promotable ? "link-color" : "link-gray"}
                  onClick={() => togglePromotable(row)}
                >
                  {row.promotable ? "Promotable" : "Exploration only"}
                </Button>
                <Button
                  size="sm"
                  color="link-destructive"
                  onClick={() => remove(row)}
                >
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ol>
      )}

      <div className="flex items-end gap-3">
        <Select
          aria-label={`Add to ${label}`}
          placeholder="Add a destination"
          selectedKey={toAdd}
          onChange={(key) => setToAdd(key == null ? null : String(key))}
          items={destinations.map((d) => ({
            id: String(d.id) + d.kind,
            label: d.name,
          }))}
          className="flex-1"
        >
          {(item) => (
            <Select.Item key={item.id} id={item.id}>
              {item.label}
            </Select.Item>
          )}
        </Select>
        <Button size="sm" color="secondary" onClick={add} isDisabled={!toAdd}>
          Add
        </Button>
      </div>
    </section>
  )
}
